import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export interface TimelineStepProps {
	title: string;
	step: number;
	current: boolean;
	completed: boolean;
	children?: ReactNode;
	isLast?: boolean;
}

interface TimelineComponentProps {
	stepList: TimelineStepProps[];
	children: ReactNode;
	onBack?: () => void;
	hideBackButton?: boolean;
	/** Preenche a altura do outlet sem estourar a viewport (fluxo profissional). */
	fillHeight?: boolean;
	/** Menos padding e sem flex-1 (agendamento PRO). */
	compactSpacing?: boolean;
	/** Oculta "Passo X de Y" e a barra de progresso (fluxo PRO com passo único). */
	hideStepProgress?: boolean;
}

const TimelineLayout = ({
	stepList,
	children,
	onBack,
	hideBackButton = false,
	fillHeight = false,
	compactSpacing = false,
	hideStepProgress = false,
}: TimelineComponentProps) => {
	const navigate = useNavigate();
	const stepsToRender = stepList.filter((step) => step.title !== 'Sucesso');

	const totalSteps = stepsToRender.length || 1;
	const currentIndex = stepsToRender.findIndex((step) => step.current);
	const completedCount = stepsToRender.filter(
		(step) => step.completed,
	).length;

	// Passo exibido para o usuário (1, 2, 3...)
	let currentStepNumber: number;
	if (currentIndex >= 0) {
		currentStepNumber = currentIndex + 1;
	} else if (completedCount > 0) {
		currentStepNumber = Math.min(completedCount, totalSteps);
	} else {
		currentStepNumber = 1;
	}

	// Progresso visual baseado no passo atual (passo 1 = 1/3, passo 2 = 2/3, etc.)
	let progressBase: number;
	if (currentIndex >= 0) {
		// Quando há um passo atual, usamos ele diretamente.
		progressBase = currentIndex + 1;
	} else if (completedCount > 0) {
		// Em telas como "Sucesso", usamos quantos já foram concluídos.
		progressBase = Math.min(completedCount, totalSteps);
	} else {
		progressBase = 1;
	}

	const progressPercent = Math.round(
		(Math.min(progressBase, totalSteps) / totalSteps) * 100,
	);

	const currentTitle =
		currentIndex >= 0 ? stepsToRender[currentIndex]?.title : undefined;

	return (
		<div
			className={
				fillHeight
					? 'flex h-full min-h-0 w-full flex-1 overflow-hidden'
					: compactSpacing
					  ? 'flex w-full'
					  : 'flex w-full flex-1'
			}
		>
			<div
				className={
					fillHeight
						? 'mx-auto flex h-full min-h-0 w-full max-w-[1400px] flex-col overflow-hidden px-2 py-4 duration-300 md:space-y-5 md:px-4 md:py-5'
						: compactSpacing
						  ? 'mx-auto flex w-full max-w-[1800px] flex-col gap-4 px-2 pb-2 pt-4 duration-300 md:gap-5 md:px-4 md:pb-3 md:pt-5'
						  : 'animate-in fade-in slide-in-from-right-4 mx-auto flex w-full max-w-[1400px] flex-col space-y-6 px-2 py-6 duration-300 md:space-y-8 md:p-8'
				}
			>
				{/* Cabeçalho padrão (seta + título + breadcrumb + progresso) */}
				<div
					className={
						fillHeight
							? 'flex shrink-0 flex-col gap-4 md:flex-row md:items-center md:justify-between'
							: 'flex flex-col gap-6 md:flex-row md:items-center md:justify-between'
					}
				>
					<div className="flex items-center gap-4 md:gap-6">
						{!hideBackButton && currentTitle !== 'Pagamento' && (
							<button
								type="button"
								onClick={() =>
									onBack ? onBack() : navigate(-1)
								}
								className="group flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200 hover:shadow-sm"
							>
								<ArrowLeft
									size={20}
									className="text-stone-400 group-hover:text-stone-900"
								/>
							</button>
						)}
						<div className="min-w-0 space-y-0.5">
							<h1 className="truncate text-2xl font-black tracking-tight text-stone-900">
								Agendar consulta
							</h1>
							<div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-300">
								<span>CONSULTAS</span>
								<span className="text-stone-200">/</span>
								<span className="text-stone-900">AGENDAR</span>
							</div>
						</div>
					</div>

					{!hideStepProgress && (
						<div className="flex w-full items-center gap-3 md:max-w-xs md:justify-end">
							<p className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-700 md:text-xs">
								Passo {currentStepNumber} de {totalSteps}
							</p>

							<div className="min-w-0 flex-1 overflow-hidden rounded-full bg-zinc-200">
								<div
									className="h-1.5 rounded-full bg-brand-orange transition-all duration-300 ease-out md:h-2"
									style={{ width: `${progressPercent}%` }}
								/>
							</div>
						</div>
					)}
				</div>

				{/* Conteúdo da Página */}
				<div
					className={
						fillHeight
							? 'mt-4 flex min-h-0 flex-1 justify-center overflow-hidden md:mt-5'
							: compactSpacing
							  ? 'flex w-full justify-center'
							  : 'ease mt-8 flex justify-center duration-200 md:mt-10'
					}
				>
					{children}
				</div>
			</div>
		</div>
	);
};

export { TimelineLayout };
